import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import { useInvertedLocalize } from '../../../../utils/localization';

import invertedMessages from './messages';

import './CardsScrollNav.css';

const MAX_VISIBLE_DOTS = 5;
const WHEEL_THRESHOLD = 60;
// Minimum horizontal finger travel (px) before a touch swipe pages the columns.
const SWIPE_THRESHOLD = 40;
const ATTR_COLS_SELECTOR = '.Tile-attributeCols';
const ATTR_SELECTOR = '.Tile-attribute';
// Overflow below this (px) is treated as "fits" — ignores the last column's
// ~15px resize-handle overhang so the scroll UI stays hidden when all cols fit.
const SCROLL_EPSILON = 20;

/**
 * Finds the row with the most attribute columns and returns it with its count.
 */
const getWidestCol = (container) => {
  const allCols = container.querySelectorAll(ATTR_COLS_SELECTOR);
  let widestCol = null;
  let maxCount = 0;

  allCols.forEach((col) => {
    const count = col.querySelectorAll(ATTR_SELECTOR).length;

    if (count > maxCount) {
      maxCount = count;
      widestCol = col;
    }
  });

  return { widestCol, maxCount, allCols };
};

/**
 * Sets scrollLeft on all attribute column rows and the scrollbar proxy.
 */
const syncScrollPosition = (allCols, scrollbar, targetScroll) => {
  allCols.forEach((col) => {
    // eslint-disable-next-line no-param-reassign -- DOM scrollLeft assignment
    col.scrollLeft = targetScroll;
  });

  if (scrollbar) {
    // eslint-disable-next-line no-param-reassign -- DOM scrollLeft assignment
    scrollbar.scrollLeft = targetScroll;
  }
};

/**
 * Shared horizontal scroll navigation for attribute columns.
 * Renders dot indicators + count at the bottom, and left/right arrow buttons
 * at the viewport vertical center. Syncs scroll across all .Tile-attributeCols rows.
 */
function CardsScrollNav({
  containerRef,
  data,
  colsToShow,
  columnWidths,
  setColumnWidths,
  activeTabKey,
  isTriggeredFromSearchOrFilter,
}) {
  const l = useInvertedLocalize(invertedMessages);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const [scrollCount, setScrollCount] = useState({ visible: 0, total: 0 });
  const [activeDotIndex, setActiveDotIndex] = useState(0);
  const scrollbarRef = useRef(null);
  const scrollbarInnerRef = useRef(null);
  const dotsWrapperRef = useRef(null);
  const isSyncingRef = useRef(false);
  const isButtonScrollingRef = useRef(false);
  const columnWidthsRef = useRef(columnWidths);
  // Skips the first tab/filter reset so mounting mid-scroll is not undone.
  const didResetMountRef = useRef(false);

  // Keep ref in sync with prop
  columnWidthsRef.current = columnWidths;

  // Update scrollbar proxy dimensions. Does NOT set the arrow top (see
  // updateArrowAnchor) — recomputing it per scroll frame makes arrows drift.
  const updateScrollbarDimensions = useCallback(() => {
    const container = containerRef.current;

    if (!container) return;

    const { widestCol } = getWidestCol(container);

    if (!widestCol || !scrollbarInnerRef.current || !scrollbarRef.current)
      return;

    scrollbarInnerRef.current.style.width = `${widestCol.scrollWidth}px`;

    const colRect = widestCol.getBoundingClientRect();

    container.style.setProperty('--cards-scroll-nav-left', `${colRect.left}px`);
    container.style.setProperty(
      '--cards-scroll-nav-right',
      `${window.innerWidth - colRect.right}px`
    );
  }, [containerRef]);

  // Anchor arrows at the tiles-area midpoint clamped to the visible viewport
  // (short list: on the records; long list: viewport centre). Recomputed only
  // on mount/resize/column/data change, never on scroll, so arrows don't drift.
  const updateArrowAnchor = useCallback(() => {
    const container = containerRef.current;

    if (!container) return;

    const scrollParent =
      container.closest('.CertsDetailsList') ||
      container.closest('[class*="DetailsList"]');

    const rect = container.getBoundingClientRect();

    // Visible band = tiles box intersected with the scroll viewport.
    const viewportTop = scrollParent
      ? scrollParent.getBoundingClientRect().top
      : 0;
    const viewportBottom = scrollParent
      ? scrollParent.getBoundingClientRect().bottom
      : window.innerHeight;

    const visibleTop = Math.max(rect.top, viewportTop, 0);
    const visibleBottom = Math.min(
      rect.bottom,
      viewportBottom,
      window.innerHeight
    );
    const top = (visibleTop + visibleBottom) / 2;

    container.style.setProperty('--cards-scroll-nav-arrow-top', `${top}px`);
  }, [containerRef]);

  // Update arrow state and column count indicator
  const updateArrowState = useCallback(() => {
    const container = containerRef.current;

    if (!container) return;

    const { widestCol, maxCount } = getWidestCol(container);

    if (!widestCol || maxCount === 0) return;

    const { scrollLeft, scrollWidth, clientWidth } = widestCol;

    // Real overflow, ignoring the ~15px resize-handle overhang (SCROLL_EPSILON).
    const maxScroll = scrollWidth - clientWidth;
    const scrollable = maxScroll > SCROLL_EPSILON;

    setIsScrollable(scrollable);
    setCanScrollLeft(scrollable && scrollLeft > 1);
    setCanScrollRight(scrollable && scrollLeft < maxScroll - 1);

    const colsRect = widestCol.getBoundingClientRect();
    let lastVisibleIndex = 0;

    widestCol.querySelectorAll(ATTR_SELECTOR).forEach((attr, i) => {
      if (attr.getBoundingClientRect().left < colsRect.right) {
        lastVisibleIndex = i + 1;
      }
    });

    setScrollCount({ visible: lastVisibleIndex, total: maxCount });

    setActiveDotIndex(
      scrollable ? Math.round((scrollLeft / maxScroll) * (maxCount - 1)) : 0
    );
  }, [containerRef]);

  // Scrollbar scroll → sync all attribute columns
  const handleScrollbarInput = useCallback(() => {
    if (
      isSyncingRef.current ||
      isButtonScrollingRef.current ||
      !scrollbarRef.current ||
      !containerRef.current
    )
      return;

    isSyncingRef.current = true;

    const { scrollLeft } = scrollbarRef.current;
    const { allCols } = getWidestCol(containerRef.current);

    allCols.forEach((col) => {
      // eslint-disable-next-line no-param-reassign -- DOM scrollLeft assignment
      col.scrollLeft = scrollLeft;
    });
    updateArrowState();

    if (Object.keys(columnWidthsRef.current).length > 0) {
      setColumnWidths({});
    }

    isSyncingRef.current = false;
  }, [containerRef, updateArrowState, setColumnWidths]);

  // Arrow button click — carousel-style scroll: swap all visible columns at once
  const scrollAllCols = useCallback(
    (direction) => {
      const container = containerRef.current;

      if (!container) return;

      const { widestCol, allCols } = getWidestCol(container);

      if (!widestCol) return;

      const attributes = widestCol.querySelectorAll(ATTR_SELECTOR);

      if (attributes.length === 0) return;

      const firstRect = attributes[0].getBoundingClientRect();
      const colStride =
        attributes.length > 1
          ? attributes[1].getBoundingClientRect().left - firstRect.left
          : firstRect.width;

      const { scrollLeft, scrollWidth, clientWidth } = widestCol;
      const colsPerView = Math.max(1, Math.floor(clientWidth / colStride));
      const pageWidth = colsPerView * colStride;
      const currentPage = Math.round(scrollLeft / pageWidth);

      const targetScroll =
        direction === 'right'
          ? Math.min((currentPage + 1) * pageWidth, scrollWidth - clientWidth)
          : Math.max(0, (currentPage - 1) * pageWidth);

      isButtonScrollingRef.current = true;
      syncScrollPosition(allCols, scrollbarRef.current, targetScroll);
      isButtonScrollingRef.current = false;
      updateArrowState();
    },
    [containerRef, updateArrowState]
  );

  // Scroll to a specific column index (for dot click navigation)
  const scrollToIndex = useCallback(
    (index) => {
      const container = containerRef.current;

      if (!container) return;

      const { widestCol, allCols } = getWidestCol(container);

      if (!widestCol) return;

      const attributes = widestCol.querySelectorAll(ATTR_SELECTOR);

      if (attributes.length === 0) return;

      // Snap to column-aligned positions (same colStride as scrollAllCols).
      const firstRect = attributes[0].getBoundingClientRect();
      const colStride =
        attributes.length > 1
          ? attributes[1].getBoundingClientRect().left - firstRect.left
          : firstRect.width;

      const { scrollWidth, clientWidth } = widestCol;
      const maxScroll = scrollWidth - clientWidth;

      // Each dot = one column; scroll it to the left edge, snapped to colStride.
      const rawScroll =
        attributes.length > 1
          ? (index / (attributes.length - 1)) * maxScroll
          : 0;
      const targetScroll = Math.min(
        Math.round(rawScroll / colStride) * colStride,
        maxScroll
      );

      isButtonScrollingRef.current = true;
      syncScrollPosition(allCols, scrollbarRef.current, targetScroll);
      isButtonScrollingRef.current = false;
      updateArrowState();
    },
    [containerRef, updateArrowState]
  );

  // Snap all columns back to the first column on tab/filter change (CR-22973).
  const resetScroll = useCallback(() => {
    const container = containerRef.current;

    if (!container) return;

    const { allCols } = getWidestCol(container);

    isButtonScrollingRef.current = true;
    syncScrollPosition(allCols, scrollbarRef.current, 0);
    isButtonScrollingRef.current = false;
    setActiveDotIndex(0);
    updateArrowState();
  }, [containerRef, updateArrowState]);

  // Main effect: attach listeners, sync, hover detection
  useEffect(() => {
    const scrollbar = scrollbarRef.current;
    const container = containerRef.current;

    if (!scrollbar || !container) return undefined;

    scrollbar.addEventListener('scroll', handleScrollbarInput, {
      passive: true,
    });

    // Wheel/trackpad on attribute columns → navigate pages (carousel-style)
    let wheelAccumulator = 0;

    const handleWheel = (e) => {
      if (isButtonScrollingRef.current) return;
      if (!e.target.closest(ATTR_COLS_SELECTOR)) return;

      // Skip when vertical intent dominates (3:1 ratio threshold)
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX) * 3) return;

      const delta = e.deltaX || e.deltaY;

      if (delta === 0) return;

      e.preventDefault();
      wheelAccumulator += delta;

      if (Math.abs(wheelAccumulator) >= WHEEL_THRESHOLD) {
        scrollAllCols(wheelAccumulator > 0 ? 'right' : 'left');
        wheelAccumulator = 0;
      }
    };

    container.addEventListener('wheel', handleWheel, {
      capture: true,
      passive: false,
    });

    // Touch swipe pages columns (no wheel on touch; columns are overflow:hidden).
    let touchStartX = 0;
    let touchStartY = 0;
    let touchActive = false;
    let touchIsHorizontal = false;
    let touchPaged = false;

    const handleTouchStart = (e) => {
      if (isButtonScrollingRef.current) return;
      if (!e.target.closest(ATTR_COLS_SELECTOR)) return;

      const touch = e.touches[0];

      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      touchActive = true;
      touchIsHorizontal = false;
      touchPaged = false;
    };

    const handleTouchMove = (e) => {
      if (!touchActive || isButtonScrollingRef.current) return;

      const touch = e.touches[0];
      const dx = touch.clientX - touchStartX;
      const dy = touch.clientY - touchStartY;

      // Let a vertical-dominant swipe scroll the list as normal (3:1 ratio).
      if (!touchIsHorizontal && Math.abs(dy) > Math.abs(dx) * 3) {
        touchActive = false;

        return;
      }

      if (Math.abs(dx) < SWIPE_THRESHOLD) return;

      touchIsHorizontal = true;
      // Prevent the list/page from also panning once we own the gesture.
      e.preventDefault();

      // One page per swipe: page once, then wait for the finger to lift.
      if (!touchPaged) {
        scrollAllCols(dx < 0 ? 'right' : 'left');
        touchPaged = true;
      }
    };

    const handleTouchEnd = () => {
      touchActive = false;
      touchIsHorizontal = false;
      touchPaged = false;
    };

    container.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    container.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('touchcancel', handleTouchEnd, {
      passive: true,
    });

    // Dimension calculation with retries for async tile rendering
    const timerId = setTimeout(() => {
      updateScrollbarDimensions();
      updateArrowAnchor();
      updateArrowState();
    }, 200);

    const timerId2 = setTimeout(() => {
      updateScrollbarDimensions();
      updateArrowAnchor();
      updateArrowState();
    }, 800);

    // Sync new tiles to current scroll position (pagination fix)
    const currentScrollLeft = scrollbar.scrollLeft;
    let syncTimerId;

    if (currentScrollLeft > 0) {
      const syncNewTiles = () => {
        container.querySelectorAll(ATTR_COLS_SELECTOR).forEach((col) => {
          if (col.scrollLeft !== currentScrollLeft) {
            // eslint-disable-next-line no-param-reassign -- DOM scrollLeft assignment
            col.scrollLeft = currentScrollLeft;
          }
        });
      };

      syncNewTiles();
      syncTimerId = setTimeout(syncNewTiles, 250);
    }

    // Show/hide arrows + dots on hover of scrollable columns or arrows
    const handleMouseOver = (e) => {
      if (
        e.target.closest(ATTR_COLS_SELECTOR) ||
        e.target.closest('.CardsScrollNav-arrow')
      ) {
        container.classList.add('Cards--colsHovered');
      }
    };

    const handleMouseOut = (e) => {
      const { relatedTarget } = e;

      if (
        relatedTarget?.closest(ATTR_COLS_SELECTOR) ||
        relatedTarget?.closest('.CardsScrollNav-arrow') ||
        relatedTarget?.closest('.CardsScrollNav-dotsWrapper')
      )
        return;

      container.classList.remove('Cards--colsHovered');
    };

    container.addEventListener('mouseover', handleMouseOver);
    container.addEventListener('mouseout', handleMouseOut);

    return () => {
      scrollbar.removeEventListener('scroll', handleScrollbarInput);
      container.removeEventListener('wheel', handleWheel, { capture: true });
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
      container.removeEventListener('mouseover', handleMouseOver);
      container.removeEventListener('mouseout', handleMouseOut);
      clearTimeout(timerId);
      clearTimeout(timerId2);
      clearTimeout(syncTimerId);
    };
  }, [
    data,
    containerRef,
    handleScrollbarInput,
    scrollAllCols,
    updateScrollbarDimensions,
    updateArrowAnchor,
    updateArrowState,
  ]);

  // Recalculate on window resize and vertical scroll
  useEffect(() => {
    // Scroll: refresh dimensions + arrow state only (leave anchor, avoids drift).
    const handleScroll = () => {
      updateScrollbarDimensions();
      updateArrowState();
    };

    // Resize/column change: geometry changed, so recompute the anchor too.
    const handleResize = () => {
      updateScrollbarDimensions();
      updateArrowAnchor();
      updateArrowState();
    };

    const container = containerRef.current;
    const scrollParent =
      container?.closest('.CertsDetailsList') ||
      container?.closest('[class*="DetailsList"]') ||
      window;

    window.addEventListener('resize', handleResize);
    scrollParent.addEventListener('scroll', handleScroll, { passive: true });

    // Observe container + column rows so a column filter that changes overflow
    // clears the stale scrollable state without waiting for a resize/scroll.
    let resizeObserver;

    if (container && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(container);
      container
        .querySelectorAll(ATTR_COLS_SELECTOR)
        .forEach((col) => resizeObserver.observe(col));
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      scrollParent.removeEventListener('scroll', handleScroll);
      resizeObserver?.disconnect();
    };
  }, [
    containerRef,
    data,
    updateScrollbarDimensions,
    updateArrowAnchor,
    updateArrowState,
  ]);

  // Serialize colsToShow ({ CATEGORY: [attrKey,...] }) so the key changes on any
  // column add/remove — a column filter changes content width, not data or box
  // size, so nothing else fires.
  const colsKey = useMemo(() => {
    if (!colsToShow) return '';

    return Object.keys(colsToShow)
      .sort()
      .map((cat) => `${cat}:${(colsToShow[cat] || []).join(',')}`)
      .join('|');
  }, [colsToShow]);

  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      updateScrollbarDimensions();
      updateArrowState();
    });
    const retryId = setTimeout(() => {
      updateScrollbarDimensions();
      updateArrowState();
    }, 250);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(retryId);
    };
  }, [colsKey, updateScrollbarDimensions, updateArrowState]);

  // Reset scroll to the first column on tab (activeTabKey) or column-filter
  // (colsKey) change. Skipped on first render so it doesn't clobber the mount
  // restore, and separate from the data-keyed effects (those must NOT reset).
  useEffect(() => {
    if (!didResetMountRef.current) {
      didResetMountRef.current = true;

      return undefined;
    }

    // rAF only; a delayed retry was dropped as it could fight a mid-window scroll.
    const rafId = requestAnimationFrame(resetScroll);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [activeTabKey, colsKey, resetScroll]);

  // Also reset on any filter/search/group-by refetch (isTriggeredFromSearchOrFilter
  // flag). Truthy-edge only so clearing it doesn't wipe a user's later scroll.
  // Covers row/smart filters, which change data not colsToShow.
  useEffect(() => {
    if (!isTriggeredFromSearchOrFilter) return undefined;

    const rafId = requestAnimationFrame(resetScroll);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [isTriggeredFromSearchOrFilter, resetScroll]);

  // Compute dot window bounds
  const { total } = scrollCount;
  const dotWindow = useMemo(() => {
    if (total <= 1) return null;

    const visibleDots = total <= 7 ? total : MAX_VISIBLE_DOTS;
    const half = Math.floor(visibleDots / 2);
    const start =
      total <= 7
        ? 0
        : Math.min(
            Math.max(0, activeDotIndex - half),
            Math.max(0, total - visibleDots)
          );

    return { start, end: start + visibleDots };
  }, [total, activeDotIndex]);

  return (
    <>
      {/* Hidden scrollbar — kept in DOM for scroll sync logic */}
      <div
        className='CardsScrollNav-track'
        ref={scrollbarRef}
        aria-hidden='true'>
        <div className='CardsScrollNav-trackInner' ref={scrollbarInnerRef} />
      </div>

      {/* Dots + count, fixed at bottom center; shown only when columns overflow */}
      {isScrollable && dotWindow && (
        <div
          className='CardsScrollNav-dotsWrapper'
          ref={dotsWrapperRef}
          role='navigation'
          aria-label={l('Column navigation')}>
          <div className='CardsScrollNav-dots'>
            {Array.from({ length: dotWindow.end - dotWindow.start }, (_, i) => {
              const dotIndex = dotWindow.start + i;
              const isActive = dotIndex === activeDotIndex;

              return (
                <button
                  type='button'
                  key={dotIndex}
                  className={`CardsScrollNav-dot${
                    isActive ? ' CardsScrollNav-dot--active' : ''
                  }`}
                  onClick={() => scrollToIndex(dotIndex)}
                  aria-current={isActive ? 'step' : undefined}
                  aria-label={l('Go to column {column}', {
                    column: dotIndex + 1,
                  })}
                />
              );
            })}
          </div>
          <span className='CardsScrollNav-count' aria-live='polite'>
            {`${scrollCount.visible}/${scrollCount.total}`}
          </span>
        </div>
      )}

      {/* Arrow buttons — vertically centered in viewport, at edges of scrollable columns */}
      <button
        type='button'
        className='CardsScrollNav-arrow CardsScrollNav-arrow--left'
        disabled={!canScrollLeft}
        onClick={() => scrollAllCols('left')}
        aria-label={l('Scroll columns left')}>
        <svg
          viewBox='0 0 24 24'
          xmlns='http://www.w3.org/2000/svg'
          aria-hidden='true'
          focusable='false'>
          <path d='M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z' />
        </svg>
      </button>
      <button
        type='button'
        className='CardsScrollNav-arrow CardsScrollNav-arrow--right'
        disabled={!canScrollRight}
        onClick={() => scrollAllCols('right')}
        aria-label={l('Scroll columns right')}>
        <svg
          viewBox='0 0 24 24'
          xmlns='http://www.w3.org/2000/svg'
          aria-hidden='true'
          focusable='false'>
          <path d='M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z' />
        </svg>
      </button>
    </>
  );
}

CardsScrollNav.propTypes = {
  containerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) })
    .isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})),
  colsToShow: PropTypes.shape({}),
  columnWidths: PropTypes.shape({}),
  setColumnWidths: PropTypes.func.isRequired,
  activeTabKey: PropTypes.string,
  isTriggeredFromSearchOrFilter: PropTypes.bool,
};

CardsScrollNav.defaultProps = {
  data: [],
  colsToShow: {},
  columnWidths: {},
  activeTabKey: undefined,
  isTriggeredFromSearchOrFilter: false,
};

export default CardsScrollNav;
